import { useEffect, useState } from "react";
import { Loader2, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { REVENUE_FIELDS, FIELD_LABELS } from "@/pages/daily_revenues/helpers";

const defaultFields = () => ({
  cash: true, card: true, wolt: false, foodora: false, kiosk: false, app: false,
});

export default function RevenueTemplateFormDialog({
  open,
  onOpenChange,
  initial,        // existing template or null
  onSaved,
}) {
  const isEdit = !!initial?._id;
  const [siteId, setSiteId] = useState("");
  const [fields, setFields] = useState(defaultFields());
  const [active, setActive] = useState(true);
  const [sites, setSites] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial?._id) {
      setSiteId(initial.site_id || "");
      setFields({ ...defaultFields(), ...(initial.fields || {}) });
      setActive(initial.active !== false);
    } else {
      setSiteId("");
      setFields(defaultFields());
      setActive(true);
    }
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/sites");
        setSites(data || []);
      } catch {/* silent */}
    })();
  }, [open]);

  const onSubmit = async () => {
    if (!siteId) {
      toast.error("Telephely kiválasztása kötelező.");
      return;
    }
    if (!Object.values(fields).some(Boolean)) {
      toast.error("Legalább egy bevétel-mezőt aktiválj.");
      return;
    }
    setSaving(true);
    try {
      const body = { site_id: siteId, fields, active };
      const resp = isEdit
        ? await api.patch(`/revenue-templates/${initial._id}`, body)
        : await api.post("/revenue-templates", body);
      toast.success(isEdit ? "Sablon frissítve." : "Sablon mentve.");
      onSaved?.(resp.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Mentés sikertelen."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="revenue-template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="h-4 w-4" />
            {isEdit ? "Sablon szerkesztése" : "Új bevétel sablon"}
          </DialogTitle>
          <DialogDescription>
            Telephely-szintű sablon. Kapcsold be a részösszegeket, amiket az adott
            telephelyen rögzíteni kell.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="site">Telephely *</Label>
            <Select value={siteId} onValueChange={setSiteId} disabled={isEdit}>
              <SelectTrigger id="site" data-testid="tpl-site-select">
                <SelectValue placeholder="Válassz telephelyet…" />
              </SelectTrigger>
              <SelectContent>
                {sites.map((s) => (
                  <SelectItem key={s._id} value={s._id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isEdit && (
              <p className="text-[11px] text-muted-foreground">
                A telephely nem módosítható meglévő sablonon.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Rögzítendő részösszegek</Label>
            <div className="rounded-lg border border-border bg-muted/10 p-2">
              {REVENUE_FIELDS.map((f) => (
                <label
                  key={f}
                  className="flex items-center justify-between rounded-md px-3 py-2.5 hover:bg-foreground/[0.03]"
                  data-testid={`tpl-field-${f}`}
                >
                  <span className="text-sm font-medium">{FIELD_LABELS[f]} összesen</span>
                  <Switch
                    checked={!!fields[f]}
                    onCheckedChange={(v) =>
                      setFields((prev) => ({ ...prev, [f]: !!v }))
                    }
                    data-testid={`tpl-switch-${f}`}
                  />
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center justify-between rounded-lg border border-border bg-muted/10 px-4 py-3">
            <div>
              <p className="text-sm font-medium">Sablon aktív</p>
              <p className="text-[11px] text-muted-foreground">
                Inaktív sablonra nem rögzíthető új napi bejegyzés.
              </p>
            </div>
            <Switch
              checked={active}
              onCheckedChange={setActive}
              data-testid="tpl-active-switch"
            />
          </label>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Mégse
          </Button>
          <Button onClick={onSubmit} disabled={saving} data-testid="save-template">
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {isEdit ? "Mentés" : "Sablon létrehozása"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
